import { Coffee, Globe, Heart, MapPin, MessageSquare, Users } from "lucide-react";
import { motion } from "framer-motion";
import { useTranslation } from "@/hooks/useTranslation";


export default function FeatureIntro() {
  const { t } = useTranslation();
  
  return (
    <div>
      <motion.section 
        className="py-24"
        initial={{ opacity: 0, y: 20 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true }}
        transition={{ duration: 0.7, delay: 0.3 }}
      >
        <div className="max-w-6xl mx-auto px-4">
          {/* 헤더 */}
          <div className="text-center mb-16">
            <h1 className="text-2xl md:text-3xl font-bold text-gray-900 mb-6">
              {t('korea_unique_internet_title_part1')} <span className="relative inline-block">
                {t('korea_unique_internet_title_highlight')}
                <svg 
                  className="absolute -bottom-3 left-0 w-full h-3" 
                  viewBox="0 0 100 8" 
                  fill="none" 
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path 
                    d="M3 5c20-0.5 40-1 60 0s35 0.5 54 0" 
                    stroke="#1f2937" 
                    strokeWidth="3.5" 
                    strokeLinecap="round" 
                    strokeLinejoin="round"
                    fill="none"
                    style={{
                      filter: 'drop-shadow(0 1px 1px rgba(0,0,0,0.1))',
                      opacity: 0.75
                    }}
                  />
                </svg>
              </span> {t('korea_unique_internet_title_part2')}
            </h1>
            <p className="text-[15px] md:text-lg text-gray-600 max-w-3xl mx-auto whitespace-pre-line">
              {t('korea_unique_internet_subtitle')}
            </p>
          </div>

          {/* 앱 비교 섹션 */}
          <motion.div 
            className="mb-16"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.7, delay: 0.3 }}
          >
            <div className="grid md:grid-cols-2 gap-12 items-center">
              {/* 글로벌 앱들 */}
              <div>
                <div className="flex items-center gap-3 mb-6">
                  <h2 className="text-xl px-2 font-bold text-gray-400">{t('global_apps')}</h2>
                </div>
                <div className="space-y-4">
                  <div className="flex items-center gap-4 p-4 bg-gray-100 rounded-xl opacity-50">
                    <div className="w-12 h-12 bg-gray-300 rounded-lg flex items-center justify-center flex-shrink-0">
                      <MapPin className="w-6 h-6 text-gray-500" />
                    </div>
                    <div>
                      <div className="font-semibold text-gray-500">Google Maps / Search</div>
                      <div className="text-sm text-gray-400">{t('google_maps_desc')}</div>
                    </div>
                  </div>
                  <div className="flex items-center gap-4 p-4 bg-gray-100 rounded-xl opacity-50">
                    <div className="w-12 h-12 bg-gray-300 rounded-lg flex items-center justify-center flex-shrink-0">
                      <MessageSquare className="w-6 h-6 text-gray-500" />
                    </div>
                    <div>
                      <div className="font-semibold text-gray-500">WhatsApp</div>
                      <div className="text-sm text-gray-400">{t('whatsapp_desc')}</div>
                    </div>
                  </div>
                  <div className="flex items-center gap-4 p-4 bg-gray-100 rounded-xl opacity-50">
                    <div className="w-12 h-12 bg-gray-300 rounded-lg flex items-center justify-center flex-shrink-0">
                      <Users className="w-6 h-6 text-gray-500" />
                    </div>
                    <div>
                      <div className="font-semibold text-gray-500">Uber</div>
                      <div className="text-sm text-gray-400">{t('uber_desc')}</div>
                    </div>
                  </div>
                </div>
              </div>

              {/* 한국 앱들 */}
              <div>
                <div className="flex items-center gap-3 mb-6">
                  <h2 className="text-xl px-2 font-bold text-gray-800">{t('korean_apps')}</h2>
                </div>
                <div className="space-y-4">
                  <div className="flex items-center gap-4 p-4 bg-green-50 border-2 border-green-200 rounded-xl">
                    <div className="w-12 h-12 bg-green-500 rounded-lg flex items-center justify-center flex-shrink-0">
                      <span className="text-white font-bold text-sm">N</span>
                    </div>
                    <div>
                      <div className="font-semibold text-gray-900">Naver Map</div>
                      <div className="text-sm text-green-600">{t('naver_map_desc')}</div>
                    </div>
                  </div>
                  <div className="flex items-center gap-4 p-4 bg-yellow-50 border-2 border-yellow-200 rounded-xl">
                    <div className="w-12 h-12 bg-yellow-500 rounded-lg flex items-center justify-center flex-shrink-0">
                      <span className="text-white font-bold text-sm">K</span>
                    </div>
                    <div>
                      <div className="font-semibold text-gray-900">KakaoTalk</div>
                      <div className="text-sm text-yellow-600">{t('kakaotalk_desc')}</div>
                    </div>
                  </div>
                  <div className="flex items-center gap-4 p-4 bg-blue-50 border-2 border-blue-200 rounded-xl">
                    <div className="w-12 h-12 bg-blue-500 rounded-lg flex items-center justify-center flex-shrink-0">
                      <span className="text-white font-bold text-sm">B</span>
                    </div>
                    <div>
                      <div className="font-semibold text-gray-900">Baemin</div>
                      <div className="text-sm text-blue-600">{t('baedal_desc')}</div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </motion.div>
        </div>
      </motion.section>
      <motion.section 
        className="bg-gradient-to-br from-blue-50 to-indigo-50 p-4 py-7 md:p-12 rounded-3xl mb-16"
        initial={{ opacity: 0, y: 30 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true }}
        transition={{ duration: 0.7, delay: 0.5 }}
      >
        <div className="max-w-5xl mx-auto px-4">
          {/* 스토리 섹션 */}
          <div className="">
            <div className="grid md:grid-cols-2 gap-24 items-center">
              <div>
                <div className="flex items-center gap-3 mb-6">
                  <Heart className="w-6 h-6 text-red-500" />
                  <span className="text-sm font-medium text-gray-600">{t('local_heart_badge')}</span>
                </div>
                <h2 className="text-xl md:text-3xl font-bold text-gray-900 mb-6 leading-10 whitespace-pre-line">
                  {t('local_heart_title')}
                </h2>
                <p className="text-gray-600 leading-relaxed mb-6 whitespace-pre-line">
                  {t('local_heart_description')}
                </p>
                <div className="text-sm text-gray-500 leading-6 whitespace-pre-line">
                  {t('local_heart_note')}
                </div>
              </div>
              
              <div className="space-y-6">
                <div className="bg-white p-6 rounded-xl shadow-sm">
                  <div className="flex items-center gap-3 mb-3">
                    <Coffee className="w-5 h-5 text-amber-600" />
                    <span className="font-semibold text-gray-900">{t('local_recommendation')}</span>
                  </div>
                  <p className="text-gray-600 text-sm">
                    {t('local_recommendation_desc')}
                  </p>
                </div>
                
                <div className="bg-white p-6 rounded-xl shadow-sm">
                  <div className="flex items-center gap-3 mb-3">
                    <MapPin className="w-5 h-5 text-green-600" />
                    <span className="font-semibold text-gray-900">{t('accurate_location')}</span>
                  </div>
                  <p className="text-gray-600 text-sm break-words">
                    {t('accurate_location_desc')}
                  </p>
                </div>
                
                <div className="bg-white p-6 rounded-xl shadow-sm">
                  <div className="flex items-center gap-3 mb-3">
                    <Users className="w-5 h-5 text-blue-600" />
                    <span className="font-semibold text-gray-900">{t('cultural_understanding')}</span>
                  </div>
                  <p className="text-gray-600 text-sm">
                    {t('cultural_understanding_desc')}
                  </p>
                </div>
              </div>
            </div>
          </div>

        </div>
      </motion.section>
    </div>
  );
}